import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserOrganization } from "@/hooks/use-user-organization";
import { useAuth } from "@/contexts/AuthContext";

export interface Order {
  id: string;
  total_cameras: number;
  total_images: number;
  status: string;
  order_type?: string;
  customer_user_id?: string;
  organization_id?: string;
  created_at: string;
  updated_at: string;
  allocated_device_ids?: string[];
  fulfillment_status?: string;
  fulfillment_notes?: string;
  tracking_number?: string;
  shipped_at?: string;
  estimated_delivery_date?: string;
  order_data?: any;
  skip_device_allocation?: boolean;
  sale?: {
    id: string;
    total_amount: number;
    status: string;
    currency?: string;
    payment_due_date?: string;
    invoice_number?: string;
    invoice_sent_at?: string;
    deal?: {
      id: string;
      title: string;
      contact_id?: string;
      contact?: {
        id: string;
        first_name?: string;
        last_name?: string;
        email?: string;
        organization?: {
          id: string;
          name: string;
          relationship_status?: string;
        };
      };
    };
  };
}

export const useOrderManagement = () => {
  const { data: userOrganizationId, isLoading: isLoadingOrg } = useUserOrganization();
  const { session } = useAuth();

  // Direct role query to bypass useRoleBasedAccess timing issues
  const directRoleQuery = useQuery({
    queryKey: ["direct-user-role", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      
      const { data } = await supabase
        .from('user_roles')
        .select('role, organization_id')
        .eq('user_id', session.user.id)
        .maybeSingle();
      
      console.log("🎯 [OrderManagement] Direct role query result:", data);
      return data;
    },
    enabled: !!session?.user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  const directIsSystemAdmin = directRoleQuery.data?.role === 'superuser';
  const isRoleLoading = directRoleQuery.isLoading;

  return useQuery({
    queryKey: ['order-management', userOrganizationId, directIsSystemAdmin],
    queryFn: async () => {
      if (directIsSystemAdmin) {
        console.log("🔍 [OrderManagement] Fetching ALL orders for superuser");
      } else {
        console.log("🔍 [OrderManagement] Fetching orders filtered by organization:", userOrganizationId);
        
        if (!userOrganizationId) {
          console.log("❌ No organization ID found, returning empty array");
          return [];
        }
      }
      
      let query = supabase
        .from('orders')
        .select(`
          *,
          sale:sales!inner (
            id,
            total_amount,
            status,
            currency,
            payment_due_date,
            invoice_number,
            invoice_sent_at,
            deal:crm_deals!inner (
              id,
              title,
              contact_id,
              contact:profiles (
                id,
                first_name,
                last_name,
                email,
                organization:organizations (
                  id,
                  name,
                  relationship_status
                )
              )
            )
          )
        `);

      // Only add organization filter for non-superusers
      if (!directIsSystemAdmin && userOrganizationId) {
        query = query.eq('organization_id', userOrganizationId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error("❌ Error fetching orders:", error);
        throw error;
      }
      
      if (directIsSystemAdmin) {
        console.log(`📊 Fetched ${data?.length || 0} orders (ALL organizations):`, data?.map(order => ({
          id: order.id.slice(0, 8),
          customerUserId: order.customer_user_id,
          organizationId: order.organization_id,
          status: order.status,
          orderType: order.order_type
        })));
      } else {
        console.log(`📊 Fetched ${data?.length || 0} orders for organization ${userOrganizationId}:`, data?.map(order => ({
          id: order.id.slice(0, 8),
          customerUserId: order.customer_user_id,
          organizationId: order.organization_id,
          status: order.status,
          orderType: order.order_type
        })));
      }
      
      return data as Order[];
    },
    enabled: !isRoleLoading && (directIsSystemAdmin || (!isLoadingOrg && !!userOrganizationId)),
  });
};

export const useUpdateOrderFulfillment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      orderId,
      fulfillmentStatus,
      fulfillmentNotes,
      trackingNumber,
      shippedAt,
      estimatedDeliveryDate,
      fulfillmentAssignedTo,
      fulfillmentStartedAt,
      fulfillmentCompletedAt
    }: {
      orderId: string;
      fulfillmentStatus?: string;
      fulfillmentNotes?: string;
      trackingNumber?: string;
      shippedAt?: string;
      estimatedDeliveryDate?: string;
      fulfillmentAssignedTo?: string;
      fulfillmentStartedAt?: string;
      fulfillmentCompletedAt?: string;
    }) => {
      console.log("🔄 Updating order fulfillment:", {
        orderId,
        fulfillmentStatus,
        fulfillmentNotes,
        trackingNumber,
        shippedAt,
        estimatedDeliveryDate,
        fulfillmentAssignedTo,
        fulfillmentStartedAt,
        fulfillmentCompletedAt
      });

      const updateData: any = { updated_at: new Date().toISOString() };
      
      if (fulfillmentStatus !== undefined) updateData.fulfillment_status = fulfillmentStatus;
      if (fulfillmentNotes !== undefined) updateData.fulfillment_notes = fulfillmentNotes;
      if (trackingNumber !== undefined) updateData.tracking_number = trackingNumber;
      if (shippedAt !== undefined) updateData.shipped_at = shippedAt;
      if (estimatedDeliveryDate !== undefined) updateData.estimated_delivery_date = estimatedDeliveryDate;
      if (fulfillmentAssignedTo !== undefined) updateData.fulfillment_assigned_to = fulfillmentAssignedTo;
      if (fulfillmentStartedAt !== undefined) updateData.fulfillment_started_at = fulfillmentStartedAt;
      if (fulfillmentCompletedAt !== undefined) updateData.fulfillment_completed_at = fulfillmentCompletedAt;

      const { data, error } = await supabase
        .from('orders')
        .update(updateData)
        .eq('id', orderId)
        .select()
        .single();

      if (error) {
        console.error("❌ Error updating order fulfillment:", error);
        throw error;
      }

      console.log("✅ Order fulfillment updated successfully:", data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-management'] });
      toast({
        title: "Order Updated",
        description: "Order fulfillment has been updated successfully.",
      });
    },
    onError: (error) => {
      console.error("❌ Failed to update order fulfillment:", error);
      toast({
        title: "Update Failed",
        description: "Failed to update order fulfillment. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateSaleStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      saleId,
      status,
      invoiceNumber,
      invoiceSentAt
    }: {
      saleId: string;
      status: string;
      invoiceNumber?: string;
      invoiceSentAt?: string;
    }) => {
      console.log("🔄 Updating sale status:", { saleId, status, invoiceNumber, invoiceSentAt });

      const updateData: any = { 
        status,
        updated_at: new Date().toISOString()
      };
      
      if (invoiceNumber !== undefined) updateData.invoice_number = invoiceNumber;
      if (invoiceSentAt !== undefined) updateData.invoice_sent_at = invoiceSentAt;

      const { data, error } = await supabase
        .from('sales')
        .update(updateData)
        .eq('id', saleId)
        .select()
        .single();

      if (error) {
        console.error("❌ Error updating sale status:", error);
        throw error;
      }

      console.log("✅ Sale status updated successfully:", data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-management'] });
      toast({
        title: "Sale Updated",
        description: "Sale status has been updated successfully.",
      });
    },
    onError: (error) => {
      console.error("❌ Failed to update sale status:", error);
      toast({
        title: "Update Failed",
        description: "Failed to update sale status. Please try again.",
        variant: "destructive",
      });
    },
  });
};